/**
 * Task store.
 *
 * Owns the active workspace's task list for `TasksPage`. Follows
 * `knowledgeStore.ts`'s `loaded`/`init`/`refresh`/`guarded` recipe.
 */
import { create } from "zustand";
import type { Task, TaskPriority } from "@/types";
import { taskRepository } from "@/services/storage/taskRepository";
import { logger } from "@/services/logger";
import { isTauri } from "@/utils";
import { useAppStore } from "./appStore";
import { useSettingsStore } from "./settingsStore";

const log = logger.scope("task-store");

interface TaskState {
  tasks: Task[];
  loaded: boolean;

  init: () => Promise<void>;
  refresh: () => Promise<void>;
  create: (input: {
    title: string;
    notes?: string;
    dueAt?: number | null;
    priority?: TaskPriority;
    projectId?: string | null;
    conversationId?: string | null;
  }) => Promise<void>;
  update: (
    id: string,
    patch: { title?: string; notes?: string; dueAt?: number | null; priority?: TaskPriority; projectId?: string | null },
  ) => Promise<void>;
  setCompleted: (id: string, completed: boolean) => Promise<void>;
  remove: (id: string) => Promise<void>;
}

async function guarded(action: () => Promise<void>, failureTitle: string): Promise<void> {
  try {
    await action();
  } catch (err) {
    log.error(failureTitle, err);
    useAppStore.getState().addToast({
      title: failureTitle,
      description: err instanceof Error ? err.message : undefined,
      variant: "error",
    });
  }
}

export const useTaskStore = create<TaskState>()((set, get) => ({
  tasks: [],
  loaded: false,

  init: async () => {
    if (get().loaded || !isTauri()) {
      set({ loaded: true });
      return;
    }
    await get().refresh();
  },

  refresh: async () => {
    const workspaceId = useSettingsStore.getState().activeWorkspaceId;
    try {
      const tasks = await taskRepository.list(workspaceId);
      set({ tasks, loaded: true });
    } catch (err) {
      log.error("Failed to load tasks", err);
      set({ loaded: true });
    }
  },

  create: async (input) => {
    const trimmed = input.title.trim();
    if (!trimmed) return;
    const workspaceId = useSettingsStore.getState().activeWorkspaceId;
    await guarded(async () => {
      await taskRepository.create({ ...input, title: trimmed, workspaceId });
      await get().refresh();
    }, "Couldn't create task");
  },

  update: async (id, patch) => {
    await guarded(async () => {
      await taskRepository.update(id, patch);
      await get().refresh();
    }, "Couldn't update task");
  },

  setCompleted: async (id, completed) => {
    await guarded(async () => {
      await taskRepository.setCompleted(id, completed);
      await get().refresh();
    }, "Couldn't update task");
  },

  remove: async (id) => {
    set((s) => ({ tasks: s.tasks.filter((t) => t.id !== id) }));
    await guarded(() => taskRepository.delete(id), "Couldn't delete task");
  },
}));
